import { agoShort } from '../status/agoText';
import type {
  AttentionCategory,
  ComponentView,
  ManagedResource,
  OnboardingPlan,
  ProjectDeployment,
  ResourceBinding,
  Server,
} from '../../api';
import { componentHealthLabel, labelFor, resourceStatusLabel } from '../../labels';

/**
 * projectHealth derives everything the Overview answers — is this project
 * healthy, what is deployed, what changed, what needs me — from records the
 * control plane already returns. Nothing here invents a metric: every value
 * is read from a resource, a component, a deployment, a recovery point, a
 * readiness row or an incident, and anything unknown stays "not known"
 * rather than being rounded up to healthy.
 *
 * Kept as pure functions so the rules are unit-testable without rendering.
 */

export type Level = 'ok' | 'warning' | 'critical' | 'unknown';

/** The server's attention level of an environment (not its site's health, CO-150). */
export type ProjectHealth = 'healthy' | 'degraded' | 'critical' | 'unknown';


export interface AttentionItem {
  key: string;
  severity: 'critical' | 'warning';
  /** Which question it answers: the live site, a deployment, the infrastructure… (CO-150). */
  category?: AttentionCategory;
  /** The server's kind, for placing items from servers older than categories. */
  kind?: string;
  /** What is wrong, in one line. */
  title: string;
  /** The fact behind it — a reading or a reason. */
  detail?: string;
  /** Where the person fixes it. */
  action?: { label: string; to: string };
  /**
   * When it started, shown in its own column rather than inside the
   * sentence (CO-81): "Opened", "Since", "Failing since", "Failed". Items
   * that describe the current state (a readiness row) have none.
   */
  when?: { label: string; at: string };
  /** Signals with the same cause, folded under this item (CO-95). */
  related?: { key: string; title: string; detail?: string }[];
  /** Advisory: may be dismissed, against this evidence (observation 5). */
  dismissible?: boolean;
  fingerprint?: string;
}

const kindWords: Record<string, string> = {
  mysql: 'MySQL',
  mariadb: 'MariaDB',
  redis: 'Redis',
  opensearch: 'OpenSearch',
  elasticsearch: 'Elasticsearch',
  rabbitmq: 'RabbitMQ',
  object_storage: 'Object storage',
  reverse_proxy: 'Web entry point',
  docker_runtime: 'Docker',
  php_runtime: 'PHP',
  frontend_runtime: 'Node.js',
};

const roleWords: Record<string, string> = {
  database: 'Database',
  cache_default: 'Cache',
  cache_page: 'Page cache',
  session: 'Sessions',
  search: 'Search',
  queue: 'Queue',
  media_storage: 'Media storage',
};

export interface ServiceRow {
  key: string;
  /** What it is for ("Database"), falling back to what it is ("MariaDB"). */
  name: string;
  /** The product and version, when known. */
  detail: string;
  level: Level;
  /** The exact word the API used, so the row never over-claims. */
  statusLabel: string;
}

const resourceLevel: Record<string, Level> = {
  reachable: 'ok',
  unreachable: 'critical',
  pending: 'unknown',
  unknown: 'unknown',
};

/**
 * serviceRows turns the environment's resource registry into one row per
 * service, named by the role it fills for Magento where a binding says so.
 */
export function serviceRows(resources: ManagedResource[], bindings: ResourceBinding[] = []): ServiceRow[] {
  const rolesOf = new Map<string, string[]>();
  for (const binding of bindings) {
    const words = rolesOf.get(binding.resource_id) ?? [];
    const word = roleWords[binding.role];
    if (word && !words.includes(word)) words.push(word);
    rolesOf.set(binding.resource_id, words);
  }
  return resources.map((resource) => {
    const roles = rolesOf.get(resource.id) ?? [];
    const kindWord = kindWords[resource.kind] ?? resource.kind;
    const detail = [roles.length > 0 ? kindWord : '', resource.observed_version ?? ''].filter(Boolean).join(' ');
    return {
      key: resource.id,
      name: roles.length > 0 ? roles.join(', ') : kindWord,
      detail,
      level: resourceLevel[resource.status] ?? 'unknown',
      statusLabel: labelFor(resourceStatusLabel, resource.status),
    };
  });
}

export interface LevelCounts {
  ok: number;
  warning: number;
  critical: number;
  unknown: number;
  total: number;
}

export function countLevels(levels: Level[]): LevelCounts {
  const counts: LevelCounts = { ok: 0, warning: 0, critical: 0, unknown: 0, total: levels.length };
  for (const level of levels) counts[level] += 1;
  return counts;
}

export interface StorefrontRow {
  key: string;
  name: string;
  /** The canonical public hostname, when one is bound. */
  hostname?: string;
  level: Level;
  statusLabel: string;
}

const componentLevel: Record<string, Level> = {
  healthy: 'ok',
  warning: 'warning',
  critical: 'critical',
  disabled: 'unknown',
  unknown: 'unknown',
  not_monitored: 'unknown',
};

/**
 * storefrontRows lists what the public actually reaches: every component
 * that answers on a hostname. A headless project shows its frontends; a
 * project whose Magento renders its own storefront shows Magento.
 */
export function storefrontRows(components: ComponentView[]): StorefrontRow[] {
  return components
    .filter((component) => component.domains.length > 0)
    .map((component) => {
      const canonical = component.domains.find((domain) => domain.canonical) ?? component.domains[0];
      return {
        key: component.application.id,
        name: component.application.name,
        hostname: canonical?.hostname,
        level: componentLevel[component.health] ?? 'unknown',
        statusLabel: labelFor(componentHealthLabel, component.health),
      };
    });
}

/** The commit, branch and outcome of the newest deployment, if there is one. */
export function latestDeployment(deployments: ProjectDeployment[]): ProjectDeployment | null {
  return deployments.length > 0 ? deployments[0]! : null;
}

const liveStatuses = new Set(['completed', 'succeeded']);

/** How long a finished deployment took, as a short human string. */
export function deploymentDuration(deployment: ProjectDeployment): string | null {
  if (!deployment.started_at || !deployment.completed_at) return null;
  const ms = new Date(deployment.completed_at).getTime() - new Date(deployment.started_at).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  if (minutes < 60) return rest === 0 ? `${minutes}m` : `${minutes}m ${rest}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

/** relativeTime keeps timestamps readable without a date library. */
export function relativeTime(iso: string | null | undefined): string {
  // The short form the whole product uses: "now", "7m ago", "2h ago", "3d ago".
  return agoShort(iso);
}

export function formatBytes(bytes: number | undefined | null): string {
  if (!bytes || bytes <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** index;
  return `${value >= 10 || index === 0 ? Math.round(value) : value.toFixed(1)} ${units[index]}`;
}

export interface SetupStep {
  key: string;
  /** The state once done: "Repository access configured". */
  label: string;
  /** What to do while it is not: "Give Commerce Ops access to the repository" (UX-12). */
  todo: string;
  /** True only for a step that is actually done (never for "not needed"). */
  done: boolean;
  /** "not_needed": the environment was set up without a plan, so the plan
   * steps do not apply — shown as such, never ticked and never counted. */
  state: 'done' | 'todo' | 'not_needed';
}

export interface SetupProgress {
  steps: SetupStep[];
  done: number;
  total: number;
  /** True once the environment's applications actually run on a server. */
  complete: boolean;
  /** The next thing to do, as an instruction, when setup is not finished. */
  next: string | null;
  /** The key of that step, to route to where it is done. */
  nextKey: string | null;
  /** The latest plan's lifecycle, when there is a plan (CO-73). */
  planLifecycle: OnboardingPlan['lifecycle'] | null;
  /** What the latest plan offers next for its setup run, if anything. */
  runAction: OnboardingPlan['run_action'] | null;
}

/**
 * setupProgress turns the real preconditions of "set up this environment on
 * a server" into a checklist.
 *
 * Completion is a completed setup run (CO-73). Approving a plan already
 * records where each application will run — placements — so a placement is
 * not proof of anything: treating it as "set up" is what made an approved,
 * never-run plan read as "Setup complete" and hid its Run action. The API
 * reports when a live setup run of the environment last completed. An
 * environment with placements and no plan at all was set up another way
 * (an existing server connected over SSH) and keeps counting as set up.
 */
export function setupProgress(input: {
  servers: Server[];
  repositoryUrl: string;
  repositoryAccessConfigured: boolean;
  plan: OnboardingPlan | null;
  placementCount: number;
}): SetupProgress {
  const plan = input.plan;
  const planUsable = Boolean(plan) && plan!.status !== 'needs_input' && plan!.status !== 'blocked';
  const ran = plan ? Boolean(plan.environment_setup_completed_at) : input.placementCount > 0;
  // Set up another way (placements, no plan): the plan steps do not apply.
  // Ticking "Plan approved" while "Setup plan made" stayed empty was the
  // contradiction the audit found (CO-87).
  const withoutPlan = ran && !plan;
  const step = (key: string, label: string, todo: string, done: boolean, planStep = false): SetupStep => {
    const state = planStep && withoutPlan ? 'not_needed' : done ? 'done' : 'todo';
    return { key, label, todo, done: state === 'done', state };
  };
  const steps: SetupStep[] = [
    step('repository', 'Magento repository set', 'Set the Magento repository', input.repositoryUrl.trim() !== ''),
    step('access', 'Repository access configured', 'Give Commerce Ops read access to the repository', input.repositoryAccessConfigured),
    step('server', 'A server is connected', 'Connect a server', input.servers.length > 0),
    step('plan', 'Setup plan made', 'Make the setup plan', Boolean(plan), true),
    step('answers', 'Plan has everything it needs', 'Answer what the plan still needs', planUsable, true),
    step('approved', 'Plan approved', 'Review and approve the plan', plan?.status === 'approved' || (Boolean(plan) && ran), true),
    step('ran', 'Setup ran on the server', 'Run the setup on the server', ran),
  ];
  const applicable = steps.filter((s) => s.state !== 'not_needed');
  const done = applicable.filter((s) => s.done).length;
  return {
    steps,
    done,
    total: applicable.length,
    complete: ran,
    next: applicable.find((s) => !s.done)?.todo ?? null,
    nextKey: applicable.find((s) => !s.done)?.key ?? null,
    planLifecycle: plan?.lifecycle ?? null,
    runAction: plan?.run_action ?? null,
  };
}

/**
 * architectureLabel names the shape of the project the way the wizard and
 * the glossary do, never "traditional".
 */
export function architectureLabel(architecture: string | undefined, storefront?: string): string {
  if (architecture === 'headless') return 'Headless';
  // The storefront technology the repository shows (docs/HYVA_THEMES.md).
  return storefront === 'hyva' ? 'Hyvä storefront' : 'Magento storefront';
}

/** magentoVersion prefers the exact pinned release over the constraint. */
export function magentoVersion(plan: OnboardingPlan | null): string | null {
  const analysis = plan?.repository_analysis;
  if (!analysis || !analysis.is_magento) return null;
  if (analysis.magento_version) return `Magento ${analysis.magento_version}`;
  if (analysis.magento_version_constraint) return `Magento ${analysis.magento_version_constraint}`;
  return null;
}

/**
 * deployedBranch is the branch the environment is actually serving: the
 * branch of the newest deployment that went live, not the newest attempt.
 */
export function deployedBranch(deployments: ProjectDeployment[]): string | null {
  const live = deployments.find((deployment) => liveStatuses.has(deployment.status) || deployment.status === 'completed_with_failures');
  return live?.source_branch || null;
}

/** "10:42 (2 h ago)" today, "Sep 22, 10:42 (8 days ago)" before. */
export function sinceWords(iso: string): string {
  const at = new Date(iso);
  const sameDay = at.toDateString() === new Date().toDateString();
  const clock = sameDay
    ? at.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    : at.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  return `${clock} (${relativeTime(iso)})`;
}
